import { CustomBlockMeta, Languages, Resource } from 'idea-toolbox';

import { User } from '../models/user.model';
import { SessionType } from './session.model';
import {
  DEFAULT_SESSION_PROPOSAL_FORM_DEF,
  DEFAULT_SESSION_PROPOSAL_FORM_DEF_PER_TYPE
} from './sessionProposalFormDefaults';

import { ServiceLanguages } from './serviceLanguages.enum';

export const LANGUAGES = new Languages({ default: ServiceLanguages.English, available: [ServiceLanguages.English] });
const DEFAULT_SESSION_REGISTRATION_BUFFER_MINUTES = 10;
const DEFAULT_MAX_SESSION_PROPOSALS_PER_USER = 3;
const DEFAULT_MAX_SPEAKERS_PER_SESSION_PROPOSAL = 3;

/**
 * The information of a proposed speaker that the admins can set as mandatory (name and email always are).
 */
export type ProposedSpeakerConfigurableFields = 'bio' | 'imageURI' | 'linkedIn';
/**
 * For each configurable information of a proposed speaker, whether it's mandatory.
 */
export type MandatorySpeakerFields = { [field in ProposedSpeakerConfigurableFields]: boolean };
const DEFAULT_MANDATORY_SPEAKER_FIELDS: MandatorySpeakerFields = { bio: true, imageURI: true, linkedIn: false };

export class Configurations extends Resource {
  static PK = 'EGM';

  /**
   * A fixed primary key.
   */
  PK: string;
  /**
   * Whether the registrations are open for ESNers.
   */
  isRegistrationOpenForESNers: boolean;
  /**
   * Whether externals and guests can register.
   */
  isRegistrationOpenForExternals: boolean;
  /**
   * Whether participants can register for sessions.
   */
  areSessionRegistrationsOpen: boolean;
  /**
   * The minimum amount of time (in minutes) a user must leave open between sessions.
   */
  sessionRegistrationBuffer: number;
  /**
   * Whether the delegation leaders can assign spots.
   */
  canCountryLeadersAssignSpots: boolean;
  /**
   * A custom block containing the definition of custom sections and fields for the registration form.
   */
  registrationFormDef: CustomBlockMeta;
  /**
   * The currency for the event, expressed in three letters (e.g. EUR).
   */
  currency: string;
  /**
   * The possible spot types.
   */
  spotTypes: string[];
  /**
   * The price for each spot type.
   */
  pricePerSpotTypes: Record<string, number>;
  /**
   * The stripe payment link for each spot type, if any.
   */
  stripeLinkPerSpotType: Record<string, string>;
  /**
   * The list of all the current ESN countries.
   */
  sectionCountries: string[];
  /**
   * The minimum number of sessions that a user has to register to per day.
   */
  minSessionPerDay: number;
  /**
   * The maximum number of sessions that a user can register to.
   */
  maxNrOfSessions: number;
  /**
   * Apply the min & max limits only for Participants role.
   */
  forParticipants: boolean;
  /**
   * Apply the min & max limits only for Externals role.
   */
  forExternals: boolean;
  /**
   * Apply the min & max limits only for Speakers role.
   */
  forSpeakers: boolean;
  /**
   * Whether externals can propose sessions.
   */
  isSessionProposalOpen: boolean;
  /**
   * The maximum number of (non-rejected) session proposals a user can submit.
   */
  maxSessionProposalsPerUser: number;
  /**
   * The maximum number of speakers (moderator excluded) in a session proposal.
   */
  maxSpeakersPerSessionProposal: number;
  /**
   * Which information of the speakers of a session proposal (other than name and email) is mandatory.
   */
  sessionProposalMandatorySpeakerFields: MandatorySpeakerFields;
  /**
   * A custom block containing the definition of the common sections and fields of the session proposal form.
   */
  sessionProposalFormDef: CustomBlockMeta;
  /**
   * The definition of the additional sections and fields of the session proposal form, specific to each session type.
   */
  sessionProposalFormDefPerType: { [type: string]: CustomBlockMeta };

  load(x: any): void {
    super.load(x);
    this.PK = Configurations.PK;
    this.isRegistrationOpenForESNers = this.clean(x.isRegistrationOpenForESNers, Boolean);
    this.isRegistrationOpenForExternals = this.clean(x.isRegistrationOpenForExternals, Boolean);
    this.areSessionRegistrationsOpen = this.clean(x.areSessionRegistrationsOpen, Boolean);
    this.sessionRegistrationBuffer = this.clean(
      x.sessionRegistrationBuffer,
      Number,
      DEFAULT_SESSION_REGISTRATION_BUFFER_MINUTES
    );
    this.canCountryLeadersAssignSpots = this.clean(x.canCountryLeadersAssignSpots, Boolean);
    this.registrationFormDef = new CustomBlockMeta(x.registrationFormDef, LANGUAGES);
    this.currency = this.clean(x.currency, String);
    this.spotTypes = this.cleanArray(x.spotTypes, String);
    this.pricePerSpotTypes = {};
    this.stripeLinkPerSpotType = {};
    if (x.pricePerSpotTypes)
      this.spotTypes.forEach(st => (this.pricePerSpotTypes[st] = this.clean(x.pricePerSpotTypes[st], Number, 0)));
    if (x.stripeLinkPerSpotType)
      this.spotTypes.forEach(st => (this.stripeLinkPerSpotType[st] = this.clean(x.stripeLinkPerSpotType[st], String)));
    this.sectionCountries = this.cleanArray(x.sectionCountries, String);
    this.minSessionPerDay = this.clean(x.minSessionPerDay, Number, 2);
    this.maxNrOfSessions = this.clean(x.maxNrOfSessions, Number, 7);
    this.forParticipants = this.clean(x.forParticipants, Boolean);
    this.forExternals = this.clean(x.forExternals, Boolean);
    this.forSpeakers = this.clean(x.forSpeakers, Boolean);
    this.isSessionProposalOpen = this.clean(x.isSessionProposalOpen, Boolean);
    this.maxSessionProposalsPerUser = this.clean(
      x.maxSessionProposalsPerUser,
      Number,
      DEFAULT_MAX_SESSION_PROPOSALS_PER_USER
    );
    this.maxSpeakersPerSessionProposal = this.clean(
      x.maxSpeakersPerSessionProposal,
      Number,
      DEFAULT_MAX_SPEAKERS_PER_SESSION_PROPOSAL
    );
    this.sessionProposalMandatorySpeakerFields = { ...DEFAULT_MANDATORY_SPEAKER_FIELDS };
    (Object.keys(DEFAULT_MANDATORY_SPEAKER_FIELDS) as ProposedSpeakerConfigurableFields[]).forEach(f => {
      const isMandatory = x.sessionProposalMandatorySpeakerFields?.[f];
      if (isMandatory !== undefined) this.sessionProposalMandatorySpeakerFields[f] = Boolean(isMandatory);
    });
    // the defaults (from the EGM Session Proposal Form) apply until the admins save a definition, even an empty one
    this.sessionProposalFormDef = new CustomBlockMeta(
      x.sessionProposalFormDef ?? DEFAULT_SESSION_PROPOSAL_FORM_DEF,
      LANGUAGES
    );
    this.sessionProposalFormDefPerType = {};
    Object.values(SessionType)
      .filter(t => t !== SessionType.COMMON)
      .forEach(t => {
        this.sessionProposalFormDefPerType[t] = new CustomBlockMeta(
          x.sessionProposalFormDefPerType?.[t] ?? DEFAULT_SESSION_PROPOSAL_FORM_DEF_PER_TYPE[t],
          LANGUAGES
        );
      });
  }

  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.PK = safeData.PK;
  }

  validate(): string[] {
    const e = super.validate();
    this.registrationFormDef.validate(LANGUAGES).forEach(ea => e.push(`registrationFormDef.${ea}`));
    if (this.sessionRegistrationBuffer < 0) e.push('sessionRegistrationBuffer');
    if (this.maxSessionProposalsPerUser < 1) e.push('maxSessionProposalsPerUser');
    if (this.maxSpeakersPerSessionProposal < 1) e.push('maxSpeakersPerSessionProposal');
    this.sessionProposalFormDef.validate(LANGUAGES).forEach(ea => e.push(`sessionProposalFormDef.${ea}`));
    Object.entries(this.sessionProposalFormDefPerType).forEach(([type, def]) =>
      def.validate(LANGUAGES).forEach(ea => e.push(`sessionProposalFormDefPerType.${type}.${ea}`))
    );
    return e;
  }

  /**
   * Load a registration form to use in the UI.
   */
  loadRegistrationForm(registrationDef: CustomBlockMeta, existingForm?: any): CustomBlockMeta {
    return existingForm ? registrationDef.loadSections(existingForm) : registrationDef.setSectionsDefaultValues();
  }

  /**
   * Whether registrations are open based on user type
   */
  canUserRegister(user: User): boolean {
    return user.isExternal() ? this.isRegistrationOpenForExternals : this.isRegistrationOpenForESNers;
  }

  /**
   * Whether the user can propose sessions: externals only, when proposals are open and after having submitted
   * their registration to the event.
   */
  canUserProposeSession(user: User): boolean {
    return this.isSessionProposalOpen && user.isExternal() && !!user.registrationAt;
  }
  /**
   * Whether the only thing preventing the user from proposing sessions is the missing registration to the event.
   */
  isRegistrationMissingToProposeSession(user: User): boolean {
    return this.isSessionProposalOpen && user.isExternal() && !user.registrationAt;
  }

  /**
   * Returns the payment link associated with the spot type.
   */
  getSpotPaymentLink(spotType: string): string {
    if (!(this.stripeLinkPerSpotType || spotType)) return;
    return this.stripeLinkPerSpotType[spotType];
  }

  /**
   * Returns the price associated with the spot type.
   */
  getSpotPrice(spotType: string): number {
    if (!(this.pricePerSpotTypes || spotType)) return;
    return this.pricePerSpotTypes[spotType];
  }
}

/**
 * The types of email templates available.
 */
export enum EmailTemplates {
  SPOT_ASSIGNED = 'spot-assigned',
  REGISTRATION_CONFIRMED = 'registration-confirmed',
  SPOT_TRANSFERRED = 'spot-transferred',
  SPOT_RELEASED = 'spot-released',
  PROPOSAL_RECEIVED = 'proposal-received',
  PROPOSAL_APPROVED = 'proposal-approved',
  PROPOSAL_APPROVED_WITH_CONDITIONS = 'proposal-approved-conditions',
  PROPOSAL_REJECTED = 'proposal-rejected'
}

/**
 * The types of document templates available.
 */
export enum DocumentTemplates {
  INVOICE = 'INVOICE'
}
